import { useState } from "react";
import React from "react";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { logout } from "../services/authServices";
import Logger from "../utils/logger";

const AuthContext = React.createContext({
  role: "",
  token: "",
  org_id: "",
  isLoggedIn: false,
  isLastUser: false,
  dataMaxCount: "",
  currentPage: "",
  orgDataMaxCount: "",
  updateRole: () => {},
  userProfile: () => {},
  tokenValidater: () => {},
  updateLastUser: () => {},
  updateDataMaxCount: () => {},
  updateOrgDataMaxCount: () => {},
  updateOrgPageChange: () => {},
  updateUserPageChange: () => {},
});

const retrieveStoredToken = () => {
  const token = localStorage.getItem("token");
  const org_id = localStorage.getItem("org_id");
  const role = localStorage.getItem("role");
  const time = localStorage.getItem("expire_In");
  const currentPage = parseInt(localStorage.getItem("org_page"));
  return { token, org_id, role, time, currentPage };
};

export const AuthContextProvider = (props) => {
  // Utility function to clear all authentication data
  const clearAuthData = () => {
    localStorage.clear();
    setToken(null);
    setIsLoggedIn(false);
    setOrgid(null);
    setRole(null);
  };
  const tokenData = retrieveStoredToken();
  let initialToken;
  let initialOrgid;
  let initialRole;
  let initialPage;
  if (tokenData) {
    initialToken = tokenData.token;
    initialOrgid = tokenData.org_id;
    initialRole = tokenData.role;
    initialPage = tokenData.currentPage;
  }
  const [token, setToken] = useState(initialToken);
  const [role, setRole] = useState(initialRole || "user");
  const [org_id, setOrgid] = useState(initialOrgid);
  const [isLoggedIn, setIsLoggedIn] = useState(!!initialToken);
  const [isLastUser, setIsLastUser] = useState(false);
  const [dataMaxCount, setDataMaxCount] = useState("");
  const [orgDataMaxCount, setOrgDataMaxCount] = useState("");
  const [currentPage, setCurrentPage] = React.useState(
    initialPage ? initialPage : 1
  );

  const updateLastUser = async () => {
    const page = await localStorage.getItem("page");
    if (page > 1 && dataMaxCount % 10 === 1) {
      await localStorage.setItem("page", page - 1);
    }
    setIsLastUser(true);
  };

  const updateDataMaxCount = async (count) => {
    setDataMaxCount(count);
  };

  const updateOrgDataMaxCount = async (count) => {
    setOrgDataMaxCount(count);
  };
  const updateRoleHandler = (role) => {
    localStorage.setItem("role", role);
    setRole(role);
  };

  const logoutHandler = async () => {
    try {
      const res = await logout({ token: token });
      setToken(null);
      setIsLoggedIn(false);
      setOrgid(null);
      setRole(null);
      localStorage.removeItem("token");
      localStorage.removeItem("org_id");
      localStorage.removeItem("role");
      localStorage.removeItem("expire_In");
      localStorage.removeItem("page");
      localStorage.removeItem("orgPage");
    } catch (err) {
      if (
        err.response &&
        (err.response.status === 401 || err.response.status === 403)
      ) {
        setToken(null);
        setIsLoggedIn(false);
        setOrgid(null);
        setRole(null);
        localStorage.removeItem("token");
        localStorage.removeItem("org_id");
        localStorage.removeItem("role");
        localStorage.removeItem("expire_In");
        localStorage.removeItem("page");
        localStorage.removeItem("orgPage");
      } else if (
        err.response &&
        err.response.data &&
        err.response.data.message
      ) {
        toast.error(err.response.data.message);
      } else {
        // Handle network errors or other errors without response
        Logger.authError("Logout error:", err);
        // Still clear the local storage on any error during logout
        setToken(null);
        setIsLoggedIn(false);
        setOrgid(null);
        setRole(null);
        localStorage.removeItem("token");
        localStorage.removeItem("org_id");
        localStorage.removeItem("role");
        localStorage.removeItem("expire_In");
        localStorage.removeItem("page");
        localStorage.removeItem("orgPage");
      }
    }
  };

  const loginHandler = (token, org_id, role, time) => {
    setToken(token);
    setOrgid(org_id);
    setIsLoggedIn(true);
    setRole(role);
    localStorage.setItem("token", token);
    localStorage.setItem("org_id", org_id);
    localStorage.setItem("role", role);
    localStorage.setItem("expire_In", JSON.stringify(Date.now() + time * 1000));
  };

  //   const userProfileHandler = async () => {
  //     const token = await localStorage.getItem('token');
  //     try {
  //       const decodedToken = await jwtDecode(token);
  //       if (decodedToken && decodedToken.nickname) {
  //         return (decodedToken.nickname.charAt(0).toUpperCase() + decodedToken.nickname.slice(1));
  //       }
  //     } catch (error) {
  //       console.error('Error decoding JWT:', error);
  //     }
  //   }
  const validateToken = async () => {
    const token = await localStorage.getItem("token");

    if (!token) {
      return {
        isValid: false,
        message: "No token found",
      };
    }

    // Check token format - support both JWT (3 parts) and JWE (5 parts)
    // JWT: Standard JSON Web Token (header.payload.signature)
    // JWE: JSON Web Encryption (header.encryptedKey.iv.ciphertext.tag)
    Logger.authLog("Token being validated:", token.substring(0, 50) + "...");
    const tokenParts = token.split(".").length;
    Logger.authLog("Token parts count:", tokenParts);

    if (tokenParts === 5) {
      Logger.authLog(
        "Detected JWE token (5 parts) - encrypted token, cannot decode client-side"
      );
      Logger.authLog(
        "Environment likely using Auth0 or similar service with JWE tokens"
      );
      // JWE tokens are encrypted and cannot be decoded client-side
      // We'll let the API validate it
      return {
        isValid: true,
        message: "JWE token detected - validation deferred to API",
        isJWE: true,
      };
    } else if (tokenParts !== 3) {
      Logger.authError(
        "Token format invalid - expected 3 (JWT) or 5 (JWE) parts, got:",
        tokenParts
      );
      Logger.authError("Full invalid token:", token);
      clearAuthData();
      return {
        isValid: false,
        message: "Invalid token format - cleared storage",
      };
    }

    try {
      const decodedToken = await jwtDecode(token, { complete: true });
      const isTokenExpired = (await Date.now()) >= decodedToken.exp * 1000;
      if (isTokenExpired === true) {
        return {
          isValid: false,
          message: "Token has expired",
        };
      } else {
        return {
          isValid: true,
          message: "Token is still valid",
          decodedToken: decodedToken.payload,
        };
      }
    } catch (error) {
      Logger.authError("Error decoding JWT:", error);
      Logger.authError("Problematic token:", token);

      // Instead of immediately clearing auth data, let's be more graceful
      // Check if this is a temporary issue or a real corruption
      if (error.message && error.message.includes("Invalid token")) {
        Logger.authWarn(
          "Token appears corrupted, but not clearing auth data immediately"
        );
        // Don't clear auth data immediately - let the user continue
        // The API calls will fail with 401 and then we can handle it properly
        return {
          isValid: false,
          message: "Token validation failed - " + error.message,
          shouldContinue: true, // Flag to indicate we shouldn't logout immediately
        };
      }

      // Only clear auth data for serious errors
      clearAuthData();
      return {
        isValid: false,
        message: "Invalid token - cleared from storage",
      };
    }
  };

  const updateOrgPageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem("org_page", page);
  };

  const updateUserPageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem("page", page);
  };

  const tokenValue = {
    token: token,
    org_id: org_id,
    role: role,
    isLoggedIn: isLoggedIn,
    isLastUser: isLastUser,
    dataMaxCount: dataMaxCount,
    orgDataMaxCount: orgDataMaxCount,
    currentPage: currentPage,
    login: loginHandler,
    logout: logoutHandler,
    updateRole: updateRoleHandler,
    updateLastUser: updateLastUser,
    updateDataMaxCount: updateDataMaxCount,
    tokenValidater: validateToken,
    updateOrgDataMaxCount: updateOrgDataMaxCount,
    updateOrgPageChange: updateOrgPageChange,
    updateUserPageChange: updateUserPageChange,
  };

  return (
    <AuthContext.Provider value={tokenValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
